import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PreferencesProvider } from './context/PreferencesContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { SearchPage } from './pages/SearchPage';
import { StudyPage } from './pages/StudyPage';
import { VisitsPage } from './pages/VisitsPage';
import { PipelinePage } from './pages/PipelinePage';
import { DocumentsPage } from './pages/DocumentsPage';
import { AdminPage } from './pages/AdminPage';
import { AdminLandingPage } from './pages/AdminLandingPage';
import { AdminReviewsPage } from './pages/AdminReviewsPage';
import { AdminSettingsPage } from './pages/AdminSettingsPage';
import { AdminSubscribersPage } from './pages/AdminSubscribersPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { CalendarPage } from './pages/CalendarPage';
import { OffersPage } from './pages/OffersPage';
import { NetworkPage } from './pages/NetworkPage';
import { TravauxPage } from './pages/TravauxPage';
import { BordereauPage } from './pages/BordereauPage';
import { BonsCommandePage } from './pages/BonsCommandePage';
import { DossierFinancementPage } from './pages/DossierFinancementPage';
import { SettingsPage } from './pages/SettingsPage';
import { HelpPage } from './pages/HelpPage';
import LandingPage from './pages/LandingPage';
import SignupPage from './pages/SignupPage';
import PricingPage from './pages/PricingPage';
import {
  VerifyEmailPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  BillingSuccessPage,
  BillingCancelPage,
} from './pages/AuthFlowPages';
import {
  TermsOfUsePage,
  TermsOfSalePage,
  PrivacyPolicyPage,
  LegalNoticePage,
} from './pages/LegalPages';
import { Toaster } from './components/ui/sonner';
import { CacheBuster } from './components/CacheBuster';
import './index.css';

function App() {
  return (
    <AuthProvider>
      <PreferencesProvider>
        <CacheBuster />
        <BrowserRouter>
          <Routes>
            {/* PUBLIC */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/register" element={<Navigate to="/signup" replace />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/cgu" element={<TermsOfUsePage />} />
            <Route path="/cgv" element={<TermsOfSalePage />} />
            <Route path="/confidentialite" element={<PrivacyPolicyPage />} />
            <Route path="/mentions-legales" element={<LegalNoticePage />} />

            {/* AUTHENTIFIÉ — Billing flow */}
            <Route path="/pricing" element={<ProtectedRoute bypassPaywall><PricingPage /></ProtectedRoute>} />
            <Route path="/billing/success" element={<ProtectedRoute bypassPaywall><BillingSuccessPage /></ProtectedRoute>} />
            <Route path="/billing/cancel" element={<ProtectedRoute bypassPaywall><BillingCancelPage /></ProtectedRoute>} />

            {/* DASHBOARD APP */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="recherche" element={<SearchPage />} />
              <Route path="etude" element={<StudyPage />} />
              <Route path="visites" element={<VisitsPage />} />
              <Route path="pipeline" element={<PipelinePage />} />
              <Route path="travaux" element={<TravauxPage />} />
              <Route path="bordereau" element={<BordereauPage />} />
              <Route path="bons-commande" element={<ProtectedRoute allowArtisan><BonsCommandePage /></ProtectedRoute>} />
              <Route path="dossier-financement" element={<DossierFinancementPage />} />
              <Route path="calendrier" element={<CalendarPage />} />
              <Route path="offres" element={<OffersPage />} />
              <Route path="reseau" element={<NetworkPage />} />
              <Route path="artisans" element={<Navigate to="/app/reseau" replace />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="parametres" element={<ProtectedRoute allowArtisan bypassPaywall><SettingsPage /></ProtectedRoute>} />
              <Route path="aide" element={<HelpPage />} />
              <Route path="admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
              <Route path="admin/landing" element={<ProtectedRoute adminOnly bypassPaywall><AdminLandingPage /></ProtectedRoute>} />
              <Route path="admin/reviews" element={<ProtectedRoute adminOnly bypassPaywall><AdminReviewsPage /></ProtectedRoute>} />
              <Route path="admin/settings" element={<ProtectedRoute adminOnly bypassPaywall><AdminSettingsPage /></ProtectedRoute>} />
              <Route path="admin/subscribers" element={<ProtectedRoute adminOnly bypassPaywall><AdminSubscribersPage /></ProtectedRoute>} />
              <Route path="reviews" element={<ProtectedRoute><ReviewsPage /></ProtectedRoute>} />
            </Route>

            {/* Backward compat : anciennes URLs sans /app/ → redirigées */}
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />
            <Route path="/recherche" element={<Navigate to="/app/recherche" replace />} />
            <Route path="/etude" element={<Navigate to="/app/etude" replace />} />
            <Route path="/visites" element={<Navigate to="/app/visites" replace />} />
            <Route path="/pipeline" element={<Navigate to="/app/pipeline" replace />} />
            <Route path="/travaux" element={<Navigate to="/app/travaux" replace />} />
            <Route path="/bordereau" element={<Navigate to="/app/bordereau" replace />} />
            <Route path="/bons-commande" element={<Navigate to="/app/bons-commande" replace />} />
            <Route path="/calendrier" element={<Navigate to="/app/calendrier" replace />} />
            <Route path="/offres" element={<Navigate to="/app/offres" replace />} />
            <Route path="/alertes" element={<Navigate to="/app/recherche" replace />} />
            <Route path="/reseau" element={<Navigate to="/app/reseau" replace />} />
            <Route path="/documents" element={<Navigate to="/app/documents" replace />} />
            <Route path="/parametres" element={<Navigate to="/app/parametres" replace />} />
            <Route path="/aide" element={<Navigate to="/app/aide" replace />} />
            <Route path="/admin" element={<Navigate to="/app/admin" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </PreferencesProvider>
    </AuthProvider>
  );
}

export default App;
